/**
 * Australian aerodrome positions (MB decision 222): where a NOTAM is placed when its text gives no position, as the
 * Diurnal tool placed it at its aerodrome from an OurAirports subset (`aircraft-map.js` `_notamBuildNaipsRecord`
 * 11601, `files/au-aerodromes.json`; inventory 04 §3.4). The data is OurAirports' airports.csv (public domain,
 * the Unlicense), built by `tools/data/ourairports-aerodromes.mjs` into `au-aerodromes.json`, which records its
 * source, licence and download date. Positions are OurAirports' aerodrome reference points; none is invented.
 */
import data from './au-aerodromes.json';

export type AerodromeType = 'large' | 'medium' | 'small' | 'heliport' | 'seaplane';

export interface Aerodrome {
  /** The four-letter code NAIPS prints, e.g. "YMEK". */
  readonly code: string;
  readonly name: string;
  readonly lat: number;
  readonly lon: number;
  readonly type: AerodromeType;
}

/** Looks up an aerodrome by its code; undefined when the list has none. */
export interface AerodromeLookup {
  get(code: string): Aerodrome | undefined;
}

/** Where the aerodrome list came from, for the identify rows and the help. */
export const AU_AERODROMES_SOURCE: {
  readonly source: string;
  readonly licence: string;
  readonly downloaded: string;
} = { source: data.source, licence: data.licence, downloaded: data.downloaded };

type Row = readonly [number, number, string, string];

let built: Map<string, Aerodrome> | null = null;

function aerodromes(): Map<string, Aerodrome> {
  if (!built) {
    built = new Map(
      Object.entries(data.aerodromes as unknown as Record<string, Row>).map(([c, [lat, lon, name, type]]) => [
        c,
        { code: c, name, lat, lon, type: type as AerodromeType },
      ]),
    );
  }
  return built;
}

/** The OurAirports list of Australian aerodromes (2,006 codes in the download of 19 Oct 2026). */
export const AU_AERODROMES: AerodromeLookup = {
  get: (code: string): Aerodrome | undefined => aerodromes().get(code.toUpperCase()),
};

/** Every aerodrome of the list, in code order (the nearest-aerodrome default, decision 278). */
export const auAerodromeList = (): readonly Aerodrome[] => [...aerodromes().values()];
